/* GHALIA — Wishlist side drawer (Ti Sento-style list) */
(() => {
  const drawer = document.querySelector('[data-wishlist-drawer]');
  if (!drawer) return;

  const WISHLIST_KEY = 'ghalia-wishlist';
  const bodyEl = drawer.querySelector('[data-wishlist-drawer-body]');
  const listEl = drawer.querySelector('[data-wishlist-drawer-list]');
  const loadingEl = drawer.querySelector('[data-wishlist-drawer-loading]');
  const emptyEl = drawer.querySelector('[data-wishlist-drawer-empty]');
  const footerEl = drawer.querySelector('[data-wishlist-drawer-footer]');
  const titleEl = drawer.querySelector('[data-wishlist-drawer-title]');
  const addAllBtn = drawer.querySelector('[data-wishlist-add-all]');

  const trashIconSvg =
    '<svg class="icon icon--trash" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h16"/><path d="M10.5 8Q12 5.25 13.5 8"/><path d="M8 8v10.5a2.5 2.5 0 0 0 2.5 2.5h3a2.5 2.5 0 0 0 2.5-2.5V8"/><path d="M11 11.75v4.5"/><path d="M13 11.75v4.5"/></svg>';

  const wishlistTitleBase = () => {
    const fromData = titleEl?.dataset?.wishlistTitleBase?.trim();
    if (fromData) return fromData;
    const plain = (titleEl?.textContent || 'Wishlist').replace(/\s*\(\d+\)\s*$/, '').trim();
    if (titleEl && plain) titleEl.dataset.wishlistTitleBase = plain;
    return plain || 'Wishlist';
  };

  const labels = {
    loading: drawer.dataset.wishlistLabelLoading || 'Loading…',
    emptyTitle: drawer.dataset.wishlistLabelEmptyTitle || '',
    emptyText: drawer.dataset.wishlistLabelEmptyText || '',
    addToBag: drawer.dataset.wishlistLabelAddToBag || 'Add to bag',
    addAll: drawer.dataset.wishlistLabelAddAll || 'Add all to bag',
    remove: drawer.dataset.wishlistLabelRemove || 'Remove'
  };

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let renderToken = 0;
  let renderTimer;

  const readWishlist = () => {
    if (window.GhaliaWishlist?.read) return window.GhaliaWishlist.read();
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    if (window.GhaliaWishlist?.write) {
      window.GhaliaWishlist.write(items);
      return;
    }
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
  };

  const itemKey = (productId, variantId) => `${productId}:${variantId || ''}`;

  const handleFromUrl = (url) => {
    if (!url) return '';
    try {
      const path = url.startsWith('http') ? new URL(url).pathname : url;
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const resolveHandle = (item) => {
    if (item.productHandle) return String(item.productHandle).trim();
    return handleFromUrl(item.productUrl);
  };

  const uniqueEntries = (items) => {
    const entries = [];
    const seen = new Set();
    items.forEach((item) => {
      const id = String(item.productId || '');
      const vid = String(item.variantId || '');
      const key = itemKey(id, vid);
      if (!id || seen.has(key)) return;
      seen.add(key);
      entries.push({
        item: { ...item, quantity: Math.max(1, parseInt(item.quantity, 10) || 1) },
        handle: resolveHandle(item)
      });
    });
    return entries;
  };

  const formatPrice = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return (Number(cents) / 100).toFixed(2);
  };

  const fetchWithTimeout = async (url, ms = 4000) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      return await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
    } finally {
      clearTimeout(timer);
    }
  };

  const fetchProductJson = async (handle) => {
    if (!handle) return null;
    try {
      const res = await fetchWithTimeout(`${root}products/${encodeURIComponent(handle)}.js`, 4000);
      if (!res.ok) return null;
      return res.json();
    } catch (_) {
      return null;
    }
  };

  const setView = (state) => {
    if (loadingEl) loadingEl.hidden = state !== 'loading';
    if (emptyEl) emptyEl.hidden = state !== 'empty';
    if (listEl) listEl.hidden = state !== 'list';
    if (footerEl) footerEl.hidden = state !== 'list';
  };

  const updateTitle = (count) => {
    if (!titleEl) return;
    const base = wishlistTitleBase();
    titleEl.textContent = count > 0 ? `${base} (${count})` : base;
  };

  const countValidProducts = (entries) => {
    const ids = new Set();
    entries.forEach(({ item, handle }) => {
      if (handle && item?.productId) ids.add(String(item.productId));
    });
    return ids.size;
  };

  const persistQty = (productId, variantId, quantity) => {
    const items = readWishlist();
    const key = itemKey(productId, variantId);
    const idx = items.findIndex((i) => itemKey(i.productId, i.variantId) === key);
    if (idx === -1) return;
    items[idx].quantity = Math.max(1, quantity);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
  };

  const removeItem = (productId, variantId) => {
    if (window.GhaliaWishlist?.remove) {
      window.GhaliaWishlist.remove({ productId, variantId });
      return;
    }
    const items = readWishlist().filter((i) => itemKey(i.productId, i.variantId) !== itemKey(productId, variantId));
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
  };

  const addToBag = (variantId, quantity, trigger, openCart = false) => {
    document.dispatchEvent(
      new CustomEvent('ghalia:cart:add-variant', {
        detail: { variantId, quantity: quantity || 1, trigger, openCart }
      })
    );
  };

  const buildLineHtml = (product, entry) => {
    const { item } = entry;
    const variant =
      product.variants?.find((v) => String(v.id) === String(item.variantId)) ||
      product.variants?.find((v) => v.available) ||
      product.variants?.[0];
    const variantId = variant?.id || '';
    const qty = item.quantity || 1;
    const price = variant?.price ?? product.price;
    const compareAt = variant?.compare_at_price;
    const img = product.featured_image || product.images?.[0] || '';
    const imgSrc = img ? (img.includes('?') ? `${img}&width=200` : `${img}?width=200`) : '';
    const safeTitle = product.title.replace(/"/g, '&quot;');
    const priceHtml =
      compareAt && compareAt > price
        ? `<del>${formatPrice(compareAt)}</del> ${formatPrice(price)}`
        : formatPrice(price);

    return `
<li class="wishlist-line" data-wishlist-line data-product-id="${product.id}" data-variant-id="${variantId}">
  <a class="wishlist-line__media" href="${product.url}">
    ${imgSrc ? `<img src="${imgSrc}" alt="${safeTitle}" width="88" height="88" loading="lazy" decoding="async">` : ''}
  </a>
  <div class="wishlist-line__body">
    <a class="wishlist-line__title" href="${product.url}" dir="auto">${product.title}</a>
    <div class="wishlist-line__price" dir="auto">${priceHtml}</div>
    <div class="wishlist-line__actions">
      <div class="qty" data-wishlist-qty>
        <button type="button" data-wishlist-qty-step="-1" aria-label="Decrease quantity">−</button>
        <input type="number" min="1" value="${qty}" data-wishlist-qty-input aria-label="Quantity">
        <button type="button" data-wishlist-qty-step="1" aria-label="Increase quantity">+</button>
      </div>
      <button class="btn btn--secondary btn--sm wishlist-line__add" type="button" data-wishlist-line-add="${variantId}" data-wishlist-line-qty="${qty}" dir="auto">${labels.addToBag}</button>
    </div>
  </div>
  <button class="wishlist-line__remove" type="button" data-wishlist-line-remove="${product.id}" data-wishlist-variant-remove="${variantId}" aria-label="${labels.remove}">${trashIconSvg}</button>
</li>`;
  };

  const showEmptyWishlist = (purgeStorage = false) => {
    updateTitle(0);
    setView('empty');
    if (purgeStorage) writeWishlist([]);
  };

  const render = async () => {
    const token = ++renderToken;
    const raw = readWishlist();
    const entries = uniqueEntries(raw).filter((e) => e.handle);

    if (!entries.length) {
      showEmptyWishlist(raw.length > 0);
      return;
    }

    updateTitle(countValidProducts(entries));
    setView('loading');

    try {
      const products = await Promise.all(entries.map((e) => fetchProductJson(e.handle)));
      if (token !== renderToken) return;

      const validPairs = entries
        .map((entry, i) => (products[i] ? { entry, product: products[i] } : null))
        .filter(Boolean);

      if (!validPairs.length) {
        showEmptyWishlist(true);
        return;
      }

      if (validPairs.length !== raw.length) {
        writeWishlist(validPairs.map(({ entry }) => entry.item));
      }

      const html = validPairs.map(({ product, entry }) => buildLineHtml(product, entry)).join('');

      if (listEl) listEl.innerHTML = html;
      updateTitle(countValidProducts(validPairs.map(({ entry }) => entry)));
      setView('list');
      document.dispatchEvent(new CustomEvent('ghalia:wishlist:sync-buttons', { detail: { header: false } }));
    } catch (err) {
      console.error('[wishlist-drawer]', err);
      if (token === renderToken) showEmptyWishlist(true);
    }
  };

  const queueRender = () => {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, 80);
  };

  const openDrawer = () => {
    if (window.GhaliaUI?.openWishlistDrawer) {
      window.GhaliaUI.openWishlistDrawer();
      return;
    }
    document.querySelector('[data-wishlist-header-btn][data-drawer-open]')?.click();
  };

  bodyEl?.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-wishlist-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      removeItem(removeBtn.dataset.wishlistLineRemove, removeBtn.dataset.wishlistVariantRemove);
      queueRender();
      return;
    }

    const addBtn = e.target.closest('[data-wishlist-line-add]');
    if (addBtn) {
      e.preventDefault();
      const line = addBtn.closest('[data-wishlist-line]');
      const qty = parseInt(line?.querySelector('[data-wishlist-qty-input]')?.value, 10) || 1;
      addBtn.setAttribute('aria-busy', 'true');
      addToBag(addBtn.dataset.wishlistLineAdd, qty, addBtn, true);
      addBtn.removeAttribute('aria-busy');
      return;
    }

    const stepBtn = e.target.closest('[data-wishlist-qty-step]');
    if (stepBtn) {
      e.preventDefault();
      const line = stepBtn.closest('[data-wishlist-line]');
      const input = line?.querySelector('[data-wishlist-qty-input]');
      const addLineBtn = line?.querySelector('[data-wishlist-line-add]');
      if (!input) return;
      const step = parseInt(stepBtn.dataset.wishlistQtyStep, 10) || 0;
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      input.value = String(next);
      if (addLineBtn) addLineBtn.dataset.wishlistLineQty = String(next);
      persistQty(line.dataset.productId, line.dataset.variantId, next);
    }
  });

  bodyEl?.addEventListener('change', (e) => {
    const input = e.target.closest('[data-wishlist-qty-input]');
    if (!input) return;
    const line = input.closest('[data-wishlist-line]');
    if (!line) return;
    const qty = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = String(qty);
    const addLineBtn = line.querySelector('[data-wishlist-line-add]');
    if (addLineBtn) addLineBtn.dataset.wishlistLineQty = String(qty);
    persistQty(line.dataset.productId, line.dataset.variantId, qty);
  });

  addAllBtn?.addEventListener('click', async () => {
    const lines = [...(listEl?.querySelectorAll('[data-wishlist-line-add]') || [])];
    if (!lines.length) return;
    addAllBtn.setAttribute('aria-busy', 'true');
    for (const btn of lines) {
      const line = btn.closest('[data-wishlist-line]');
      const qty = parseInt(line?.querySelector('[data-wishlist-qty-input]')?.value, 10) || 1;
      const variantId = btn.dataset.wishlistLineAdd;
      try {
        if (window.GhaliaCart?.addVariant) {
          await window.GhaliaCart.addVariant(variantId, { quantity: qty, openCart: false });
        } else {
          addToBag(variantId, qty, btn, false);
          await new Promise((r) => setTimeout(r, 400));
        }
      } catch (_) {
        /* continue with remaining items */
      }
    }
    addAllBtn.removeAttribute('aria-busy');
    document.querySelector('[data-drawer-open="[data-cart-drawer]"]')?.click();
  });

  document.addEventListener('ghalia:drawer:open', (e) => {
    if (e.detail?.drawer === drawer) queueRender();
  });

  document.addEventListener('ghalia:wishlist:update', queueRender);
  document.addEventListener('ghalia:wishlist:add', queueRender);
  document.addEventListener('ghalia:wishlist:remove', queueRender);

  const pillBtnSel = '.btn, .wishlist-line__add';
  const clearPressed = (el) => el?.classList.remove('is-pressed');
  const setPressed = (el) => {
    if (!el?.matches?.(pillBtnSel)) return;
    drawer.querySelectorAll(`${pillBtnSel}.is-pressed`).forEach(clearPressed);
    el.classList.add('is-pressed');
  };

  drawer.addEventListener(
    'pointerdown',
    (e) => {
      if (e.button !== 0) return;
      setPressed(e.target.closest(pillBtnSel));
    },
    true
  );
  drawer.addEventListener('pointerup', (e) => clearPressed(e.target.closest(pillBtnSel)), true);
  drawer.addEventListener('pointercancel', (e) => clearPressed(e.target.closest(pillBtnSel)), true);
  drawer.addEventListener('pointerleave', (e) => {
    const btn = e.target.closest?.(pillBtnSel);
    if (btn?.classList.contains('is-pressed')) clearPressed(btn);
  });

  queueRender();

  if (drawer.hasAttribute('data-wishlist-auto-open')) {
    window.addEventListener('load', () => {
      window.setTimeout(openDrawer, 120);
    });
  }
})();
